import { PaymentProcessError } from '@b2b/common/directives/payment/types';
import { Notice, NoticeFactory } from '@b2b/common/factories/notice';
import { safeApply } from '@b2b/common/rxjs';
import { AddressService, AddressServiceModule } from '@b2b/common/services/address';
import { LocationService, LocationServiceModule } from '@b2b/common/services/address/location.service';
import { ApiService } from '@b2b/common/services/api/types';
import {
  CheckoutProcessService,
  CheckoutProcessServiceModule,
} from '@b2b/common/services/checkout/checkoutProcess.service';
import { OrderEditData, OrderEditCoreData, SecureOrderEditProps } from '@b2b/common/services/checkout/edit-order.types';
import { isACMECORSCheckoutError } from '@b2b/common/services/checkout/types';
import { PaymentService, PaymentServiceModule } from '@b2b/common/services/payment/payment.service';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ValidateService } from '@b2b/common/services/validate';
import { LooseAutocomplete, TODO, UIBModalService } from '@b2b/types/core';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IPromise, IQService, ISCEService, IScope, ITimeoutService } from 'angular';
import { OrdersModalsChangeOrgControllerModule } from '../../modals/changeOrg/controllers/OrdersModalsChangeOrgController';
import { PermissionsService } from '@b2b/common/services/permissions';

export const OrdersEditControllerModule = angular
  .module('acme.view.application.orders.edit.controllers', [
    uiRouter,
    'ui.bootstrap',
    'angularUUID2',
    'acme.common.service.api',
    'acme.common.service.order',
    'acme.common.service.calendar',
    'acme.common.service.permissions',
    'acme.common.service.helpers',
    'acme.common.service.validate',
    'acme.common.service.themesHelper',
    'acme.common.factory.notice',
    'acme.common.factory.gridState',
    'acme.common.filter.saleChannel',
    'acme.common.service.userPreferences',
    OrdersModalsChangeOrgControllerModule,
    'acme.view.application.partners.modals.selectList.controllers',
    'acme.view.application.reports.create.showMessageModal.controllers',
    'acme.view.application.programs.modals.confirmSuspendedEventOrder.controllers',
    CheckoutProcessServiceModule,
    LocationServiceModule,
    PaymentServiceModule,
    AddressServiceModule,
  ])
  .controller('OrdersEditController', [
    '$scope',
    '$q',
    '$sce',
    '$state',
    '$filter',
    '$timeout',
    '$uibModal',
    'uuid2',
    'ApiService',
    'OrderService',
    'CalendarService',
    'PermissionsService',
    'HelpersService',
    'ThemesHelperService',
    'ValidateService',
    'NoticeFactory',
    'GridStateFactory',
    'OrderData',
    'ThemesData',
    'UserPreferencesService',
    'CheckoutProcessService',
    'LocationService',
    'PaymentService',
    'AddressService',
    function OrdersEditController(
      $scope: IScope & { ordersEdit: TODO; stateTimer?: IPromise<void> },
      $q: IQService & { allSettled: (promises: IPromise<any>[]) => IPromise<any[]> },
      $sce: ISCEService,
      $state: StateService,
      $filter: IFilterService,
      $timeout: ITimeoutService,
      $uibModal: UIBModalService,
      uuid2: TODO,
      ApiService: ApiService,
      OrderService: TODO,
      CalendarService: TODO,
      PermissionsService: PermissionsService,
      HelpersService: TODO,
      ThemesHelperService: ThemesHelperService,
      ValidateService: ValidateService,
      NoticeFactory: NoticeFactory,
      GridStateFactory: TODO,
      OrderData: TODO,
      ThemesData: TODO,
      UserPreferencesService: TODO,
      CheckoutProcessService: CheckoutProcessService,
      LocationService: LocationService,
      paymentService: PaymentService,
      addressService: AddressService
    ) {
      const ctrl = {
        requiresCardData: false,

        model: {
          accountId: '',
          customerId: '',
          orderId: '',
          eventItems: [],
          notes: '',
          payment: {},
          address: {},
        } as TODO,

        requestUUID: false as string | false,

        billingSameAsOrderContact: false,
        selectedOrgContact: {} as TODO,

        isEnabledCORSCheckout: false,
        corsCheckoutCreditCardMessage: `Payment will be securely collected when you select "Take Payment & Update Order"`,

        orderDetails: {
          columns: 1,
          data: 'ordersEdit.model',
          include: ['orderNumber'],
        },

        accountDetails: {
          columns: 1,
          data: 'ordersEdit.model',
          sorting: true,
          include: ['accountName'],
          mappings: {
            accountName: 'Organization Name',
          },
        },

        orderLanguages: {
          columns: 1,
          data: 'ordersEdit.model',
          include: ['preferredLanguage'],
        },

        orgOrderContactDetails: {
          columns: 1,
          data: 'ordersEdit.selectedOrgContact',
          include: ['email', 'phoneNumber'],
        },

        orderContactDetails: {
          columns: 1,
          data: 'ordersEdit.model',
          include: ['contactFirstName', 'contactLastName', 'email', 'phoneNumber'],
        },

        billingDetails: {
          columns: 1,
          data: 'ordersEdit.model',
          include: ['billingFirstName', 'billingLastName', 'billingEmail', 'billingPhoneNumber'],
          mappings: {
            billingFirstName: 'First Name',
            billingLastName: 'Last Name',
            billingEmail: 'Email',
            billingPhoneNumber: 'Phone Number',
          },
        },

        emailLanguages: [
          {
            code: 'en-nz',
            name: 'English (New Zealand)',
          },
          {
            code: 'en-us',
            name: 'English (United States)',
          },
          {
            code: 'fr-ca',
            name: 'French (Canada)',
          },
          {
            code: 'es-mx',
            name: 'Spanish (Mexico)',
          },
        ],

        emailOptions: false,

        sendEmail: true,

        lastOrg: {},

        summary: {} as TODO,

        events: {},

        addons: {},

        personTypes: {},

        orderItems: {},

        accountPaymentPolicy: {},

        isAccount: false,

        isDiscounted: false,

        ticketDelivery: '',

        isSaving: false,

        isLoaded: false,

        isProcessing: false,

        mode: 'booking',

        notice: {} as Notice,

        getItemType: OrderService.getItemType,

        themes: {},

        enableCSRToggle: false,

        dateFormat: UserPreferencesService.getDateFormat('shortDate', 'dateFormat'),

        hourFormat: UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),

        hasMembershipConversion: false,

        validateEmail: true,

        hasEmailError: false,

        init: function () {
          paymentService.processData$
            .pipe(
              safeApply($scope, {
                next: ({ chargeAmount, paymentMethod }) => {
                  ctrl.requiresCardData = paymentMethod === 'CreditCard' && chargeAmount > 0;
                },
              })
            )
            .subscribe();

          if (PermissionsService.check('CSR')) {
            this.enableCSRToggle = true;
          }

          if ($state.params.customerId) {
            this.model.customerId = $state.params.customerId;
          }

          if ($state.params.accountId && PermissionsService.check('ACCT_ORDERS_UPDATE')) {
            this.model.accountId = $state.params.accountId;
            this.isAccount = true;
            this.ticketDelivery = 'willCall';
            this.getPayment(this.model.accountId);
          } else if (PermissionsService.check('ORDER_UPDATE')) {
            this.ticketDelivery = 'print';
            this.model.accountId = null;
          } else {
            $state.go('home.default', {}, { notify: true });
          }

          $scope.$emit('Application::contentTitleChange', {
            title: 'Edit Order',
            buttonLeft: {
              label: 'Cancel',
              action: () => {
                ctrl.cancel();
              },
            },
          });

          ctrl.notice = new NoticeFactory();

          if (OrderData.data && OrderData.data.success) {
            if (OrderData.data.payload && OrderData.data.payload.accountId) {
              if (!PermissionsService.check('ACCT_ORDERS_UPDATE')) {
                console.log('ACCT_ORDERS_UPDATE');
                $state.go('home.default', {}, { notify: true });
              }
              this.getPayment(OrderData.data.payload.accountId);
            } else {
              if (!PermissionsService.check('ORDER_UPDATE')) {
                console.log('ORDER_UPDATE');
                $state.go('home.default', {}, { notify: true });
              }
            }
            this.success.init(OrderData);
          } else {
            this.error.init(OrderData);
          }

          ThemesHelperService.resetModel();

          if (ThemesData.data && ThemesData.data.success) {
            this.initThemes(ThemesData);
          }

          $scope.$on('Payment::Complete', this.onPaymentComplete);
          $scope.$on('Payment::Error', this.onPaymentError);
          $scope.$on('ContactsTypeahead::NoResults', this.onContactsTypeaheadNoResults);
          $scope.$on('ContactsTypeahead::ContactNotFound', this.onContactsTypeaheadContactNotFound);

          $scope.$on('$destroy', $scope.$watch('ordersEdit.selectedOrgContact', this.onSelectedOrgContactUpdate));
        },

        initThemes: function (response) {
          ThemesHelperService.setModel(response.data.payload);
          CheckoutProcessService.initACMECheckout();

          ctrl.themes = {
            policyTerms: $sce.trustAsHtml(ThemesHelperService.getHTMLVariable('checkout-process-page-terms')),
          };

          ctrl.isEnabledCORSCheckout = CheckoutProcessService.isEnabledCORSCheckout();
        },

        getOrderEventDetails: function (orderId) {
          ApiService.orders
            .events(orderId)
            .then((response) => ctrl.success.getOrderEventDetails(response))
            .catch((response) => ctrl.error.getOrderEventDetails(response));
        },

        getOrderAddonDetails: function () {
          const q = $q.defer();
          const queue: TODO[] = [];

          for (let i = 0, ii = ctrl.model.eventItems.length; i < ii; i++) {
            if (ctrl.model.eventItems[i].eventId === 'Addons') {
              for (let j = 0, jj = ctrl.model.eventItems[i].items.length; j < jj; j++) {
                if (ctrl.model.eventItems[i].items[j].itemTypeId) {
                  queue.push(ApiService.inventory.detail(ctrl.model.eventItems[i].items[j].itemTypeId));
                }
              }
            }
          }

          $q.allSettled(queue).then((response) => {
            var _response;

            while ((_response = response.pop())) {
              if (_response.data.error) {
              } else {
                ctrl.addons[_response.data.payload.id] = _response.data.payload;
              }
            }
            q.resolve(ctrl.addons);
          });

          return q.promise;
        },

        getPayment: function (accountId) {
          ApiService.partners.payments
            .account(accountId)
            .then((response) => ctrl.success.getPayment(response))
            .catch((response) => ctrl.error.getPayment(response));
        },

        getDuplicateOrder: function () {
          const uuid = ctrl.requestUUID ? ctrl.requestUUID : '';

          ApiService.system
            .uuidObjectRegistry({ uuid })
            .then((response) => ctrl.success.getDuplicateOrder(response))
            .catch((response) => ctrl.error.getDuplicateOrder(response));
        },

        initOrderAddonsData: function () {
          for (const orderEvent of ctrl.model.eventItems) {
            for (const eventItem of orderEvent.items) {
              if (ctrl.isInventoryItemType(eventItem.itemType)) {
                eventItem.resolvedQuantity = eventItem.quantity;

                if (!ctrl.eventHasAddOns(orderEvent.eventId)) {
                  const key = 'item-' + orderEvent.eventId + '-' + eventItem.itemTypeId + '-' + eventItem.orderItemId;
                  ctrl.personTypes[key] = ctrl.addons[eventItem.itemTypeId]; // false;
                  eventItem.salesChannels = ctrl.personTypes[key].salesChannels;

                  if (!Array.isArray(eventItem.salesChannels)) {
                    eventItem.salesChannels = [];
                  }
                }
              }
            }
          }
        },

        initOrderData: function () {
          this.model = this.processOrder(OrderData.data.payload);

          if (this.model.accountId) {
            this.isAccount = true;
          }
          if (this.model.membershipId) {
            this.isDiscounted = true;
            this.orderDetails.filters = {
              membershipId: function (v) {
                return v && v !== 'null' && v !== null ? v : false;
              },
            };
            this.orderDetails.include = this.orderDetails.include.concat(['membershipId']);
          }

          this.isLoaded = true;
        },

        resetModel: function () {
          this.model.notes = '';
          this.model.orderId = 0;
          this.model.orderItems = [];
          this.model.noEmail = false;
          this.model.payment = {};
          this.model.address = {};
        },

        getOrderBalance: function () {
          return HelpersService.currencyFloatToInteger(ctrl.model.orderBalance || 0);
        },

        getOrderDifference: function () {
          let totalDiscount = 0;

          for (const type in ctrl.personTypes) {
            if (ctrl.personTypes.hasOwnProperty(type)) {
              const itemId = type.split('-');
              const event = ctrl.findEventById(ctrl.model.eventItems, itemId[1]) || {};
              const eventItem = ctrl.findTicketTypeForEvent(event.items, itemId[2], itemId[3]);

              if (eventItem.index > -1) {
                if (ctrl.isInventoryItemType(eventItem.item.itemType)) {
                  totalDiscount +=
                    HelpersService.currencyFloatToInteger(ctrl.calculateAddOnPrice(eventItem.item)) -
                    HelpersService.currencyFloatToInteger(eventItem.item.discountedSubtotal || 0);
                } else if (ctrl.isEventItemType(eventItem.item.itemType)) {
                  totalDiscount +=
                    HelpersService.currencyFloatToInteger(ctrl.calculatePrice(eventItem.item)) -
                    HelpersService.currencyFloatToInteger(eventItem.item.discountedSubtotal || 0);
                }
              }
            }
          }

          return HelpersService.integerToCurrencyFloat(totalDiscount);
        },

        updateNotes: function () {
          //need this, but it can be empty
        },

        checkContacts: function (order) {
          var r = false,
            billingContactInfo,
            orderContactInfo;

          if (order) {
            billingContactInfo =
              (order.billingFirstName || '') +
              (order.billingLastName || '') +
              (order.billingEmail || '') +
              (order.billingPhoneNumber || '');
            orderContactInfo =
              (order.contactFirstName || '') +
              (order.contactLastName || '') +
              (order.email || '') +
              (order.phoneNumber || '');
            if (billingContactInfo === orderContactInfo) {
              r = true;
            }
          }

          return r;
        },

        changeOrg: function () {
          const changeOrgModal = $uibModal.open<TODO>({
            templateUrl: 'views/application/views/partners/modals/selectList/selectList.tpl.html',
            size: 'lg',
            controller: 'PartnersModalsSelectListController',
            resolve: {
              currentOrg: () => {
                return {
                  id: ctrl.model.accountId,
                  name: ctrl.model.accountName,
                };
              },
            },
          });

          changeOrgModal.result
            .then((org) => {
              ctrl.saveLastOrg();

              ctrl.acceptOrgChange(org);
            })
            .catch(() => {
              // Do nothing
            });
        },
        acceptOrgChange: function (organization) {
          if (this.model.accountId !== organization.company.id) {
            const showMessageModal = $uibModal.open<TODO>({
              templateUrl: 'views/application/views/orders/modals/changeOrg/changeOrg.tpl.html',
              size: 'md',
              controller: 'OrdersModalsChangeOrgController',
              resolve: {
                nextOrganization: () => organization,
                currentOrder: () => ctrl.model,
                safe: () => true,
              },
            });
            // Invoked when modal completes with close(params)
            showMessageModal.result
              .then((contact) => {
                ctrl.model.accountId = organization.company.id;
                ctrl.model.accountName = organization.company.name;
                ctrl.isAccount = true;

                if (contact.firstName) {
                  ctrl.model.contactFirstName = contact.firstName;
                }
                if (contact.lastName) {
                  ctrl.model.contactLastName = contact.lastName;
                }
                if (contact.phoneNumber) {
                  ctrl.model.phoneNumber = contact.phoneNumber;
                }
                if (contact.email) {
                  ctrl.model.email = contact.email;
                }

                if (contact.type === 'new') {
                  ctrl.addContact(contact);
                }
              })
              .catch(() => {
                ctrl.revertOrg();
              });
          }
        },

        showMessage: function (title: string, message: string, done?: TODO, cancel?: TODO) {
          if (message) {
            const showMessageModal = $uibModal.open<TODO>({
              templateUrl: 'views/application/views/reports/create/modals/showMessage.tpl.html',
              size: 'md',
              controller: 'ReportsCreateShowMessageModalController',
              resolve: {
                currentTitle: () => title,
                currentMessage: () => message,
                safe: () => true,
              },
            });

            // Invoked when modal completes with close(params)
            showMessageModal.result
              .then(() => {
                if (typeof done === 'function') {
                  done();
                }
              })
              .catch(() => {
                // Do nothing
                if (typeof cancel === 'function') {
                  cancel();
                }
              });
          }
        },

        saveLastOrg: function () {
          this.lastOrg = {
            isAccount: this.isAccount,
            accountId: this.model.accountId,
            accountName: this.model.accountName,
            email: this.model.email,
            phoneNumber: this.model.phoneNumber,
            contactFirstName: this.model.contactFirstName,
            contactLastName: this.model.contactLastName,
          };
        },

        addNewContact: function () {
          this.saveLastOrg();
          this.addContact();
        },

        addContact: function (suggestedContact) {
          const addModal = $uibModal.open<TODO>({
            templateUrl: 'views/application/views/partners/create/modals/ManageSecondaryContactModal.tpl.html',
            size: 'lg',
            controller: 'ManageSecondaryContactModalController',
            resolve: {
              ValidateService: () => ValidateService,
              alertMessage: () => suggestedContact && 'The Order Contact must be a contact at the Organization.',
              organizationId: () => ctrl.model.accountId,
              organizationName: () => ctrl.model.accountName,
              contact: () => suggestedContact || null,
              address: () => null,
            },
          });

          addModal.result.then(
            (contact) => {
              var props: TODO = {};
              //add the secondaryContact
              if (contact) {
                props = contact;
                props.organizationId = ctrl.model.accountId;
                ctrl.isAccount = true;

                ApiService.organizations.contacts
                  .create(props)
                  .then((response) => {
                    var _contact = response.data.payload;

                    ctrl.model.contactFirstName = _contact.firstName;
                    ctrl.model.contactLastName = _contact.lastName;
                    ctrl.model.phoneNumber = _contact.phoneNumber;
                    ctrl.model.email = _contact.email;

                    $scope.$broadcast('ContactsTypeahead::Refresh', { organizationId: ctrl.model.accountId });
                  })
                  .catch(() => {
                    ctrl.showMessage('Unable to create contact', 'Failed to create contact for organization.');
                  });
              }
            },
            function () {
              ctrl.revertOrg();
            }
          );
        },

        revertOrg: function () {
          this.isAccount = this.lastOrg.isAccount;
          this.model.accountId = this.lastOrg.accountId;
          this.model.accountName = this.lastOrg.accountName;
          this.model.contactFirstName = this.lastOrg.contactFirstName;
          this.model.contactLastName = this.lastOrg.contactLastName;
          this.model.phoneNumber = this.lastOrg.phoneNumber;
          this.model.email = this.lastOrg.email;
        },

        sortOrderEvents: function (a, b) {
          if (a.eventId.toLowerCase() !== 'addons') {
            return -1;
          }

          return 0;
        },

        processOrder: function (order) {
          var eventItem,
            orderEvent,
            hasMembershipConversion = false;

          order.eventItems.sort(this.sortOrderEvents);

          for (let i = 0, ii = order.eventItems.length; i < ii; i++) {
            orderEvent = order.eventItems[i];

            if (!this.enableCSRToggle) {
              orderEvent.toggleCSRPrices = true;
              orderEvent.toggleCSRAddons = true;
            }

            if (orderEvent.eventDate) {
              orderEvent.startDate = $filter('date')(CalendarService.parseDate(orderEvent.eventDate), this.dateFormat);
              orderEvent.startHour = $filter('date')(CalendarService.parseDate(orderEvent.eventDate), this.hourFormat);
            } else {
              orderEvent.startDate = 'Date N/A';
              orderEvent.startHour = 'Time N/A';
            }

            if (orderEvent.state && orderEvent.state === 'cancelled') {
              orderEvent.isCancelled = true;
              orderEvent.formattedCancellationDate = $filter('date')(
                orderEvent.cancellationDate,
                HelpersService.dateFormats.standardDateTime
              );
            } else {
              orderEvent.isCancelled = false;
              orderEvent.formattedCancellationDate = '';
            }

            if (orderEvent.state && orderEvent.state === 'suspended') {
              orderEvent.isSuspended = true;
            }

            for (let j = 0, jj = orderEvent.items.length; j < jj; j++) {
              eventItem = orderEvent.items[j];
              eventItem.resolvedQuantity = eventItem.quantity;
              eventItem.salesChannels = [];
              console.log('ProgramsOrdersEdit::processOrder', this.personTypes, i, j, eventItem.itemTypeName);
              if (this.isEventItemType(eventItem.itemType)) {
                eventItem.isDiscounted = this.isDiscountedItem(eventItem);
                console.log(
                  'ProgramsOrdersEdit::processOrder',
                  this.findEventPersonType(orderEvent.eventId, eventItem.itemTypeName)
                );
                this.personTypes[
                  'item-' + orderEvent.eventId + '-' + eventItem.itemTypeId + '-' + eventItem.orderItemId
                ] =
                  this.events[orderEvent.eventId].priceList.prices[
                    this.findEventPersonType(orderEvent.eventId, eventItem.itemTypeName)
                  ];
                eventItem.salesChannels =
                  this.personTypes[
                    'item-' + orderEvent.eventId + '-' + eventItem.itemTypeId + '-' + eventItem.orderItemId
                  ].salesChannels;
              } else if (this.isInventoryItemType(eventItem.itemType)) {
                console.log(
                  'ProgramsOrdersEdit::processOrder',
                  ctrl.findEventInventoryType(orderEvent.eventId, eventItem.itemTypeName)
                );
                if (this.eventHasAddOns(orderEvent.eventId)) {
                  this.personTypes[
                    'item-' + orderEvent.eventId + '-' + eventItem.itemTypeId + '-' + eventItem.orderItemId
                  ] =
                    this.events[orderEvent.eventId].addOns.inventoryAddOns[
                      ctrl.findEventInventoryType(orderEvent.eventId, eventItem.itemTypeName)
                    ];
                  eventItem.salesChannels =
                    this.personTypes[
                      'item-' + orderEvent.eventId + '-' + eventItem.itemTypeId + '-' + eventItem.orderItemId
                    ].salesChannels;
                } else {
                  this.personTypes[
                    'item-' + orderEvent.eventId + '-' + eventItem.itemTypeId + '-' + eventItem.orderItemId
                  ] = this.addons[eventItem.itemTypeId]; // false;
                }
              }

              hasMembershipConversion = hasMembershipConversion || this.isMembershipConversion(eventItem);
            }
          }

          this.hasMembershipConversion = hasMembershipConversion;

          order.preferredLanguage = this.emailLanguages[1];

          return order;
        },

        isDiscountedItem: function (eventItem) {
          return eventItem.discountedUnitPrice !== eventItem.unitPrice;
        },

        isMembershipConversion: function (ticket) {
          return ticket && ticket.conversionStatus && ticket.conversionStatus === 'MembershipConversion';
        },

        processOrderEvents: function (orderEvents) {
          for (let i = 0, ii = orderEvents.length; i < ii; i++) {
            if (!this.events[orderEvents[i].id]) {
              this.events[orderEvents[i].id] = orderEvents[i];
            }
          }
        },

        processSummary: function (data) {
          let order: TODO;

          for (let i = 0, ii = data.orderItemCharges.length; i < ii; i++) {
            if (data.orderItemCharges[i].orderItemId) {
              order = ctrl.findOrderItem(data.orderItemCharges[i].orderItemId);
            } else {
              //find order by ticketTypeId and eventId
              order = ctrl.findOrderItem(null, data.orderItemCharges[i].eventId, data.orderItemCharges[i].itemTypeId);
            }
            data.orderItemCharges[i].orderItem = order;
          }

          data.orderItemCharges = ctrl.sortByEventAndTicketIndex(data.orderItemCharges);

          return data;
        },

        isEventEditable: function (event: { eventId: string }) {
          if (event.eventId.toLowerCase() === 'membership') {
            return false;
          }

          return true;
        },

        isAddon: function (event: { eventId: string }) {
          return event.eventId.toLowerCase() === 'addons';
        },

        isEventItemType: function (type: LooseAutocomplete<'Event' | 'Combo'>) {
          return type === 'Event' || type === 'Combo';
        },

        isInventoryItemType: function (type: LooseAutocomplete<'Inventory' | 'ComboInventory'>) {
          return type === 'Inventory' || type === 'ComboInventory';
        },

        isNewEvent: function (index: number) {
          if (index <= 0) {
            return true;
          }

          const currentItem = this.summary.orderItemCharges[index];
          const prevItem = this.summary.orderItemCharges[index - 1];

          return currentItem.orderItem.index.event !== prevItem.orderItem.index.event;
        },

        eventHasAddOns: function (eventId) {
          return (
            ctrl.events[eventId] &&
            ctrl.events[eventId].hasOwnProperty('addOns') &&
            Array.isArray(ctrl.events[eventId].addOns.inventoryAddOns)
          );
        },

        eventIsAddOn: function (eventId) {
          return (
            ctrl.events[eventId] &&
            ctrl.events[eventId].hasOwnProperty('addOns') &&
            Array.isArray(ctrl.events[eventId].addOns.inventoryAddOns)
          );
        },

        sortByEventAndTicketIndex: function (list: TODO[]) {
          list.sort((a, b) => {
            const aOrderItemIndex = a.orderItem.index;
            const bOrderItemIndex = b.orderItem.index;
            const r = aOrderItemIndex.event - bOrderItemIndex.event;

            if (r !== 0) {
              return r;
            }

            return aOrderItemIndex.item - bOrderItemIndex.item;
          });

          return list;
        },

        findEventPersonType: function (eventId: string, type: string) {
          var r = 0;

          for (let i = 0, ii = this.events[eventId].priceList.prices.length; i < ii; i++) {
            if (this.events[eventId].priceList.prices[i].personType.name === type) {
              r = i;
              break;
            }
          }

          return r;
        },

        findEventInventoryType: function (eventId: string, name: string) {
          let r = 0;

          if (
            ctrl.events[eventId] &&
            ctrl.events[eventId].hasOwnProperty('addOns') &&
            ctrl.events[eventId].addOns.inventoryAddOns instanceof Array
          ) {
            for (let i = 0, ii = ctrl.events[eventId].addOns.inventoryAddOns.length; i < ii; i++) {
              if (ctrl.events[eventId].addOns.inventoryAddOns[i].name === name) {
                r = i;
                break;
              }
            }
          }

          return r;
        },

        findTicketTypeForEvent: function (eventItems: TODO[], ticketTypeId: string, orderItemId?: string) {
          let r: TODO = { index: -1 };
          for (let i = 0, ii = eventItems.length; i < ii; i++) {
            //intentionally using == here and not ===
            if (orderItemId) {
              if (eventItems[i].orderItemId === orderItemId && !this.isMembershipConversion(eventItems[i])) {
                r = {
                  item: eventItems[i],
                  index: i,
                };
                break;
              }
            } else if (
              (eventItems[i].ticketTypeId == ticketTypeId || eventItems[i].itemTypeId == ticketTypeId) &&
              (eventItems[i].ticketAddedToOrder || !eventItems[i].isDiscounted) &&
              !this.isMembershipConversion(eventItems[i])
            ) {
              r = {
                item: eventItems[i],
                index: i,
              };
              break;
            }
          }
          return r;
        },

        findEventById: function (list: TODO[], id: string) {
          return list.find((item) => item.eventId === id);
        },

        findOrderItem: function (orderItemId: string | null, eventId?: string, ticketTypeId?: string) {
          let r: TODO | false = false;
          const events = ctrl.model.eventItems;

          if (orderItemId) {
            for (let i = 0, ii = events.length; i < ii && r === false; i++) {
              const tickets: TODO[] = events[i].items;
              for (let j = 0, jj = tickets.length; j < jj; j++) {
                if (tickets[j].orderItemId === orderItemId) {
                  r = {
                    item: tickets[j],
                    index: {
                      event: i,
                      item: j,
                    },
                  };
                  break;
                }
              }
            }
          } else {
            for (let i = 0, ii = events.length; i < ii && r === false; i++) {
              const tickets: TODO[] = events[i].items;
              if (events[i].eventId === eventId) {
                for (let j = 0, jj = tickets.length; j < jj; j++) {
                  if (tickets[j].ticketTypeId == ticketTypeId || tickets[j].itemTypeId == ticketTypeId) {
                    r = {
                      item: tickets[j],
                      index: {
                        event: i,
                        item: j,
                      },
                    };
                    break;
                  }
                }
              }
            }
          }

          return r;
        },

        getEventTypeLength: function (eventItems, ticketType) {
          var r = -1;

          for (let i = 0, ii = eventItems.length; i < ii; i++) {
            if (eventItems[i].itemType === ticketType) {
              r++;
            }
          }

          return r;
        },

        hasPriceList: function (event) {
          return event.priceList && event.priceList.prices instanceof Array;
        },

        getEventCapacity: function (eventId) {
          var r = 0;

          if (eventId && this.events[eventId]) {
            r = this.events[eventId].capacity - this.events[eventId].soldQuantity;
          }

          return r;
        },

        isMode: function (mode) {
          return this.mode === mode;
        },

        addTicketTypeForEvent: function (event) {
          var selectedTicketType = event.selectedTicketType,
            ticketItem,
            ticket = ctrl.findTicketTypeForEvent(event.items, selectedTicketType.personType.id),
            ticketIndex = ctrl.findTicketTypeForEvent(event.items, selectedTicketType.personType.id).index;
          if (selectedTicketType) {
            // TODO: get itemType from event instead of hard coding it here to Event
            if (ticketIndex < 0 || ticket.item.isDiscounted) {
              //new ticket
              ticketItem = {
                discountedUnitPrice: selectedTicketType.price,
                itemType: 'Event',
                itemTypeName: selectedTicketType.personType.name,
                quantity: 1,
                resolvedQuantity: 0,
                ticketTypeId: selectedTicketType.personType.id,
                ticketTypeName: selectedTicketType.personType.name,
                unitPrice: selectedTicketType.price,
                salesChannels: selectedTicketType.salesChannels,
                ticketAddedToOrder: true,
              };

              event.items.push(ticketItem);

              this.personTypes['item-' + event.eventId + '-' + ticketItem.ticketTypeId] =
                this.events[event.eventId].priceList.prices[
                  this.findEventPersonType(event.eventId, ticketItem.itemTypeName)
                ];
            } else {
              //existing ticket - increment quantity
              this.incrementTicketTypeForEvent(event, ticketIndex);
            }
          }
          event.selectedTicketType = {};
        },

        addInventoryTypeForEvent: function (event) {
          var selectedTicketType = event.selectedAddOnType,
            ticketItem,
            ticketIndex = ctrl.findTicketTypeForEvent(event.items, selectedTicketType.id).index;
          if (selectedTicketType) {
            // TODO: get itemType from event instead of hard coding it here to Event
            if (ticketIndex < 0) {
              //new ticket
              ticketItem = {
                itemType: 'Inventory',
                itemTypeName: selectedTicketType.name,
                quantity: 1,
                resolvedQuantity: 0,
                itemTypeId: selectedTicketType.id,
                discountedUnitPrice: selectedTicketType.price,
                salesChannels: selectedTicketType.salesChannels,
              };

              event.items.push(ticketItem);

              this.personTypes['item-' + event.eventId + '-' + ticketItem.itemTypeId] =
                this.events[event.eventId].addOns.inventoryAddOns[
                  ctrl.findEventInventoryType(event.eventId, ticketItem.itemTypeName)
                ];
            } else {
              //existing ticket - increment quantity
              this.incrementTicketTypeForEvent(event, ticketIndex);
            }
          }
          event.selectedAddOnType = {};
        },

        incrementTicketTypeForEvent: function (event, ticketIndex) {
          var quantity = event.items[ticketIndex].quantity;
          if (isNaN(parseInt(quantity, 10))) {
            quantity = 0;
          } else {
            quantity = parseInt(quantity, 10);
          }
          event.items[ticketIndex].quantity = quantity + 1;
        },

        removeTicketTypeForEvent: function (event, ticketTypeId, orderItemId) {
          var ticket = event.items.splice(ctrl.findTicketTypeForEvent(event.items, ticketTypeId, orderItemId).index, 1);
          if (orderItemId) {
            if (this.personTypes['item-' + event.eventId + '-' + ticket.ticketTypeId] + '-' + orderItemId) {
              delete this.personTypes['item-' + event.eventId + '-' + ticket.ticketTypeId + '-' + orderItemId];
            }
          } else {
            if (this.personTypes['item-' + event.eventId + '-' + ticket.ticketTypeId]) {
              delete this.personTypes['item-' + event.eventId + '-' + ticket.ticketTypeId];
            }
          }
        },

        removeTicketQuantity: function (ticket) {
          if (ticket.quantity) {
            ticket.showRestoreQuantity = true;
            ticket.originalQuantity = ticket.quantity;
            ticket.quantity = 0;
          }
        },

        restoreTicketQuantity: function (ticket) {
          if (!ticket.quantity) {
            ticket.showRestoreQuantity = false;
            ticket.quantity = ticket.originalQuantity;
            ticket.originalQuantity = 0;
          }
        },

        validateTicket: function (ticket) {
          if (isNaN(parseInt(ticket.quantity, 10))) {
            ticket.quantity = 0;
          }
          ticket.price = ticket.quantity * ticket.discountedUnitPrice;
        },

        calculateSubTotal: function () {
          var total = 0,
            quantity = 0,
            addOnQuantity = 0;

          for (let i = 0, ii = this.model.events.length; i < ii; i++) {
            total += HelpersService.currencyFloatToInteger(this.model.events[i].total);
            quantity += this.model.events[i].quantity;
            addOnQuantity += this.model.events[i].addOnQuantity;
          }

          this.addOnQuantity = addOnQuantity;
          this.quantity = quantity;
          this.total = HelpersService.integerToCurrencyFloat(total);

          if (this.payment.type !== 'fullPayment') {
            if (this.model.accountId && this.payment.deposit) {
              if (this.payment.deposit.minAmount > 0) {
                if (this.total > this.payment.deposit.minAmount) {
                  this.paymentMinimum = this.payment.deposit.minAmount;
                } else {
                  this.paymentMinimum = this.total;
                }
              } else if (this.payment.deposit.percent > 0) {
                this.paymentMinimum = this.total * (this.payment.deposit.percent / 100);
              }
            } else {
              // Payment Policy not set so set it to the total amount
              this.paymentMinimum = total;
            }
          } else {
            this.paymentMinimum = total;
          }
        },

        calculatePrice: function (ticket) {
          var r = 0;
          if (ticket.quantity !== '') {
            this.validateTicket(ticket);
            r = ticket.price;
          }
          return r;
        },

        calculateAddOnPrice: function (addOn) {
          var r = 0;
          if (addOn.quantity !== '') {
            if (isNaN(parseInt(addOn.quantity, 10))) {
              addOn.quantity = 0;
            }
            var price = addOn.quantity * addOn.discountedUnitPrice;

            r = price || 0;
          }

          return r;
        },

        resetNonCSRItems: function (event, isEvent) {
          const non_csrItems = ($filter('saleChannel') as TODO)(event.items, '!customerRep');
          isEvent = isEvent || false;

          for (let i = 0, ii = non_csrItems.length; i < ii; i++) {
            non_csrItems[i].quantity = non_csrItems[i].resolvedQuantity;
            non_csrItems[i].showRestoreQuantity = false;
            non_csrItems[i].originalQuantity = 0;
          }
        },

        togglePrices: function (event) {
          event.toggleCSRPrices = !event.toggleCSRPrices;
          if (!event.toggleCSRPrices && this.isMode('booking')) {
            this.resetNonCSRItems(event);
          }
        },

        toggleAddons: function (event) {
          event.toggleCSRAddons = !event.toggleCSRAddons;
          if (!event.toggleCSRAddons && this.isMode('booking')) {
            this.resetNonCSRItems(event);
          }
        },

        refreshAvailableTicketsQuantity: function (event) {
          const reservedQuantity = event.items.reduce((sum, price) => {
            return sum + (price._reservedQuantity || 0);
          }, 0);

          return ApiService.inventoryB2C.detail(event.eventId).then((eventSummary) => {
            if (ctrl.events[event.eventId]) {
              ctrl.events[event.eventId].soldQuantity = eventSummary.soldQuantity - reservedQuantity;
            }
          });
        },

        selectEmailOption: function () {
          if (this.model.noEmail) {
            this.emailOption = '0';
          } else if (this.model.sendEmailToAll) {
            this.emailOption = '2';
          } else if (!this.model.noEmail && !this.model.sendEmailToAll) {
            this.emailOption = '1';
          }
        },

        setEmailOption: function () {
          switch (parseInt(this.emailOption, 10)) {
            case 0:
              this.model.noEmail = true;
              this.model.sendEmailToAll = false;
              break;
            case 1:
              this.model.noEmail = false;
              this.model.sendEmailToAll = false;
              break;
            case 2:
              this.model.noEmail = false;
              this.model.sendEmailToAll = true;
              break;
          }
        },

        updateQuantity: function (price, quantity) {
          this.notice.hideErrorMessages();
          price.quantity = quantity;
        },

        onReservationSuccess: function (price) {
          var _reserved = price.quantity - price.resolvedQuantity;
          price._reservedQuantity = _reserved < 0 ? 0 : _reserved;
          this.notice.hideErrorMessages();
        },

        onReservationError: function (message) {
          this.notice.showErrorMessages([
            {
              message: message,
            },
          ]);
        },

        validateQuantity: function (item) {
          if (isNaN(parseInt(item.quantity, 10))) {
            item.quantity = 0;
          }
        },

        getOrderItemChargesPaymentAmount: function (eventId, itemTypeId, orderItemId) {
          let item;
          let r = 0;

          for (let i = 0, ii = ctrl.summary.orderItemCharges.length; i < ii; i++) {
            item = ctrl.summary.orderItemCharges[i];
            if (orderItemId) {
              if (item.orderItem && item.orderItem.item && item.orderItem.item.orderItemId === orderItemId) {
                r = item.paymentAmount;
              }
            } else if (item.orderItem && item.orderItem.item && item.orderItem.item.itemTypeId == itemTypeId) {
              r = item.paymentAmount;
            }
          }
          return r;
        },

        getPricesForEvent: function (eventId, toggleCSRPrices) {
          var r = [];
          if (this.events[eventId]) {
            if (!toggleCSRPrices) {
              r = ($filter('saleChannel') as TODO)(this.events[eventId].priceList.prices, 'customerRep');
            } else {
              r = ($filter('saleChannel') as TODO)(this.events[eventId].priceList.prices, 'customerRep').concat(
                ($filter('saleChannel') as TODO)(this.events[eventId].priceList.prices, '!customerRep')
              );
            }
          }

          return r;
        },

        getAddOnsForEvent: function (eventId, toggleCSRAddons) {
          var r = [];
          if (this.events[eventId] && this.eventHasAddOns(eventId)) {
            if (!toggleCSRAddons) {
              r = ($filter('saleChannel') as TODO)(this.events[eventId].addOns.inventoryAddOns, 'customerRep');
            } else {
              r = ($filter('saleChannel') as TODO)(this.events[eventId].addOns.inventoryAddOns, 'customerRep').concat(
                ($filter('saleChannel') as TODO)(this.events[eventId].addOns.inventoryAddOns, '!customerRep')
              );
            }
          }
          return r;
        },

        getInventoryAddOns: function (eventId) {
          return this.events[eventId].addOns.inventoryAddOns;
        },

        getIntegerAmount: function (amount) {
          var numAmount = HelpersService.currencyFloatToInteger(amount);
          return HelpersService.integerToCurrencyFloat(Math.abs(numAmount));
        },

        getCurrentTotal: function () {
          let totalPrice = 0;

          for (const type in this.personTypes) {
            if (this.personTypes.hasOwnProperty(type)) {
              const itemId = type.split('-');
              const event = ctrl.findEventById(ctrl.model.eventItems, itemId[1]) || {};
              const eventItem = ctrl.findTicketTypeForEvent(event.items, itemId[2], itemId[3]);

              if (eventItem.index > -1) {
                if (ctrl.isInventoryItemType(eventItem.item.itemType)) {
                  if (eventItem.item.quantity && this.personTypes[type] && this.personTypes[type].price) {
                    totalPrice = totalPrice + eventItem.item.quantity * this.personTypes[type].price;
                  }
                } else if (this.isEventItemType(eventItem.item.itemType)) {
                  totalPrice = totalPrice + eventItem.item.quantity * eventItem.item.discountedUnitPrice;
                }
              }
            }
          }

          return totalPrice;
        },

        getBalanceDue: function () {
          return parseFloat(ctrl.model.orderBalance) + ctrl.getOrderDifference() || 0;
        },

        getPreviousPayment: function () {
          let totalPrice = 0;

          for (const type in this.personTypes) {
            if (this.personTypes.hasOwnProperty(type)) {
              const itemId = type.split('-');
              const event = ctrl.findEventById(ctrl.model.eventItems, itemId[1]) || {};
              const eventItem = ctrl.findTicketTypeForEvent(event.items, itemId[2], itemId[3]);

              if (eventItem.index > -1) {
                if (ctrl.isInventoryItemType(eventItem.item.itemType)) {
                  totalPrice = totalPrice + eventItem.item.resolvedQuantity * this.personTypes[type].price;
                } else if (this.isEventItemType(eventItem.item.itemType)) {
                  totalPrice = totalPrice + eventItem.item.resolvedQuantity * eventItem.item.discountedUnitPrice;
                }
              }
            }
          }

          return totalPrice - this.model.orderBalance;
        },

        processComplete: function () {
          var item, eventId;

          this.model.orderItems = [];

          this.updateOrderItems();

          this.setEmailOption();

          if (this.model.payment) {
            if (this.model.payment.contactEmail) {
              this.model.email = this.model.payment.contactEmail;
            }

            if (this.model.payment.contactFirstName) {
              this.model.payment.firstName = this.model.payment.contactFirstName;
            }

            if (this.model.payment.contactLastName) {
              this.model.payment.lastName = this.model.payment.contactLastName;
            }

            if (this.model.payment.address) {
              this.model.address = this.model.payment.address;
            }
          }

          for (item in this.orderItems) {
            if (this.orderItems.hasOwnProperty(item)) {
              this.model.orderItems.push($.extend({}, this.orderItems[item]));
            }
          }
        },

        updateOrderItems: function () {
          ctrl.orderItems = {};

          for (const type in ctrl.personTypes) {
            if (ctrl.personTypes.hasOwnProperty(type)) {
              const itemId = type.split('-');
              const event = ctrl.findEventById(ctrl.model.eventItems, itemId[1]) || {};
              const eventItem = ctrl.findTicketTypeForEvent(event.items, itemId[2], itemId[3]);

              if (eventItem.index > -1) {
                if (ctrl.isInventoryItemType(eventItem.item.itemType)) {
                  ctrl.orderItems['item-' + itemId[1] + '-' + eventItem.index] = {
                    orderItemId: eventItem.item.orderItemId,
                    quantity: eventItem.item.quantity,
                    eventId: event.eventId,
                    itemType: eventItem.item.itemType,
                    itemTypeId: ctrl.personTypes[type].id,
                  };
                } else if (ctrl.isEventItemType(eventItem.item.itemType)) {
                  ctrl.orderItems['item-' + itemId[1] + '-' + eventItem.index] = {
                    orderItemId: eventItem.item.orderItemId,
                    quantity: eventItem.item.quantity,
                    eventId: event.eventId,
                    itemType: eventItem.item.itemType,
                    itemTypeId: ctrl.personTypes[type].personType.id,
                  };
                }
              }
            }
          }
        },

        review: function () {
          ctrl.notice.hideSuccessMessages();
          ctrl.notice.hideErrorMessages();

          if (ctrl.hasTicketsonSuspendedEvents()) {
            const suspendedModal = $uibModal.open<TODO>({
              templateUrl:
                'views/application/views/programs/modals/confirmSuspendedEventOrder/confirmSuspendedEventOrder.tpl.html',
              size: 'md',
              controller: 'ProgramsModalsConfirmSuspendedEventOrderController',
              resolve: {
                suspendedMessage: function () {
                  return 'You are editing an order for a suspended event, are you sure you want to continue?';
                },
              },
            });
            suspendedModal.result.then(() => {
              ctrl.proceedWithReview();
            });
          } else {
            ctrl.proceedWithReview();
          }
        },

        proceedWithReview: function () {
          ctrl.processComplete();
          ApiService.orders
            .calculate(ctrl.model)
            .then((response) => ctrl.success.review(response))
            .catch((response) => ctrl.error.review(response));
        },

        hasTicketsonSuspendedEvents: function () {
          var r = false;

          angular.forEach(ctrl.model.eventItems, function (eventItem) {
            if (eventItem.isSuspended) {
              angular.forEach(eventItem.items, function (item) {
                if (item.resolvedQuantity < item.quantity) {
                  r = true;
                }
              });
            }
          });

          return r;
        },

        processPayment: function () {
          this.isProcessing = true;
          if (HelpersService.currencyFloatToInteger(this.summary.subtotalPaymentAmount) > 0) {
            // Send message to payment directive to process order
            this.validateEmail = parseInt(this.emailOption, 10) === 0 ? false : true;
            $scope.$broadcast('Payment::Process', { validateEmail: this.validateEmail });
            this.processPaymentErrors();
          } else {
            this.model.payment = null;
            this.complete();
          }
        },

        clearLoading() {
          ctrl.isProcessing = false;
        },

        shouldUseCORSCheckout() {
          return CheckoutProcessService.shouldUseCORSCheckout({ requiresCardData: ctrl.requiresCardData });
        },

        complete: function () {
          ctrl.processComplete();

          ctrl.notice.hideSuccessMessages();
          ctrl.notice.hideErrorMessages();

          /**
           * This is order duplication prevention
           * We create the checkout UUID and tack it onto the checkout request
           * Only rewrite the requestUUID when there are no errors and a checkout has taken place
           * This will always be refreshed when the page is refreshed, but, in the context of an order
           * it should only be overwritten if it has been explicitly set to `false`
           */
          if (!ctrl.requestUUID) {
            ctrl.requestUUID = uuid2.newuuid();
          }

          if (ctrl.selectedOrgContact) {
            ctrl.model.contactFirstName = ctrl.selectedOrgContact.firstName;
            ctrl.model.contactLastName = ctrl.selectedOrgContact.lastName;
            ctrl.model.phoneNumber = ctrl.selectedOrgContact.phoneNumber;
            ctrl.model.email = ctrl.selectedOrgContact.email;
            if (ctrl.selectedOrgContact.id) {
              ctrl.model.accountContactId = ctrl.selectedOrgContact.id;
            }
          }

          if (ctrl.shouldUseCORSCheckout()) {
            const data: OrderEditData = CheckoutProcessService.processOrderUpdateData(
              { ...ctrl.model, requestUUID: ctrl.requestUUID },
              { withCreditCardInfo: false }
            );

            CheckoutProcessService.handleACMEPaymentSessionEditOrder(ctrl.processACMEPaymentData(data))
              .then((response) => ctrl.success.complete(response))
              .catch(async (response) => {
                if (isACMECORSCheckoutError(response)) {
                  switch (response.type) {
                    case 'sdk.render_failed': {
                      ctrl.clearLoading();

                      ctrl.error.complete({
                        status: 0,
                        data: { code: 0, errorCode: 0, error: response.params.message },
                      });
                      break;
                    }

                    case 'sdk.cancel_flow': {
                      ctrl.clearLoading();
                      break;
                    }

                    case 'sdk.billing_address.request_change': {
                      ctrl.clearLoading();
                      document.querySelector(`[data-testid="billing-address-anchor"]`)?.scrollIntoView();
                      break;
                    }

                    case 'api.error': {
                      ctrl.error.complete(CheckoutProcessService.mapToPaymentProcessError(response));
                      break;
                    }

                    default: {
                      console.warn(`unhandled`, response);

                      ctrl.clearLoading();
                      break;
                    }
                  }
                } else {
                  ctrl.error.complete(response);
                }
              });
          } else {
            const data: OrderEditCoreData = CheckoutProcessService.processOrderUpdateCoreData(
              { ...ctrl.model, requestUUID: ctrl.requestUUID }
            );

            ApiService.orders
              .update(data)
              .then((response) => ctrl.success.complete(response.data.payload))

              .catch((response) => ctrl.error.complete(response));
          }
        },

        processACMEPaymentData: function (data: OrderEditData): SecureOrderEditProps {
          const countryData = LocationService.findCountry({ country: data.billingCountry });
          const stateData = LocationService.findState({ state: data.billingState });
          const cssVarsData = CheckoutProcessService.getCORSCheckoutCSSVariables();

          return {
            values: data,
            options: {
              formattedBillingAddress: addressService.formatAddress({
                streetAddress1: data.billingAddress1,
                streetAddress2: data.billingAddress2,
                country: countryData ? countryData.country.name : data.billingCountry,
                zipCode: data.billingZipCode,
                city: data.billingCity,
                state: stateData ? stateData.name : data.billingState,
              }),
              modal: {
                title: 'Payment',
                submit: 'Complete Order',
                close: 'Close',
              },
              authorizePayment: {
                paymentData: {
                  externalId: '',
                  chargeAmount: data.payment.chargeAmount ?? String(ctrl.getBalanceDue()),
                  firstName: data.billingFirstName || '',
                  lastName: data.billingLastName || '',
                  creditCardVerificationData: {
                    address1: data.billingAddress1,
                    address2: data.billingAddress2,
                    country: countryData ? String(countryData.country.numericCode) : data.billingCountry,
                    zipCode: data.billingZipCode,
                    city: data.billingCity,
                    state: stateData ? stateData.abbreviation : data.billingState,
                  },
                },
                saleChannel: this.model.accountId ? 'reseller' : 'insideSalesIndividual',
              },
              capturePayment: {
                loaderHtml: CheckoutProcessService.getCheckoutLoadingHtml(),
              },
              autorenew: {
                enabled: false,
                html: ThemesHelperService.getTextVariable('checkout-process-page-autorenew-message'),
                url: '',
              },
              terms: {
                html: ThemesHelperService.getHTMLVariable('checkout-process-page-terms'),
                url: '',
              },
              creditCardSettings: {
                supportedCardsText: ThemesHelperService.getTextVariable('checkout-supported-cards-text'),
                showVisaLogo: ThemesHelperService.getTextVariable('checkout-show-visa-logo'),
                showMcLogo: ThemesHelperService.getTextVariable('checkout-show-mc-logo'),
                showDiscoverLogo: ThemesHelperService.getTextVariable('checkout-show-discover-logo'),
                showAmexLogo: ThemesHelperService.getTextVariable('checkout-show-amex-logo'),
              },
              variables: cssVarsData,
            },
          };
        },

        cancel: function () {
          window.history.go(-1);
        },

        back: function () {
          this.mode = 'booking';
        },

        processPaymentErrors: function () {
          const paymentErrors = ValidateService.getErrorHash();

          ctrl.hasEmailError = false;

          angular.forEach(paymentErrors, function (item, index) {
            if ((index as TODO) === 'payment-email') {
              ctrl.hasEmailError = true;
            }
          });
        },

        showEmailErrorMessage: function (emailOption) {
          return this.hasEmailError && parseInt(this.emailOption, 10) === parseInt(emailOption, 10);
        },

        success: {
          init: function (response) {
            //first get order event details, then process the order itself
            ctrl.getOrderEventDetails(response.data.payload.orderId);
          },

          getOrderEventDetails: function (response) {
            //process the order after getting the order event details
            ctrl.processOrderEvents(response.data.payload);

            ctrl.initOrderData();

            ctrl.getOrderAddonDetails().then(() => {
              ctrl.initOrderAddonsData();
              ctrl.selectEmailOption();
            });
          },

          getOrderAddonDetails: function (response) {},

          getPayment: function (response) {
            ctrl.accountPaymentPolicy = response.data.payload;
          },

          review: function (response) {
            ctrl.summary = ctrl.processSummary(response.data.payload);
            ctrl.mode = 'payment';
          },

          complete: function (response) {
            const orderId = ctrl.model.orderId;

            if ($scope.stateTimer) {
              $timeout.cancel($scope.stateTimer);
            }

            ctrl.resetModel();
            $scope.$broadcast('Payment::Reset');
            $scope.stateTimer = $timeout(function () {
              $state.go('application.orders.detail.summary', { orderId: orderId });
              /**
               * State should have changed it is safe to restore on next tick
               *   requestUUID: false
               */
              // next tick
              $timeout(function () {
                ctrl.requestUUID = false;
              }, 0);
            }, 300);

            ctrl.isProcessing = false;
          },

          getDuplicateOrder: function (response) {
            ctrl.success.complete(response);
          },
        },

        error: {
          init: function (response) {},

          getOrderEventDetails: function (response) {
            ctrl.notice.showErrorMessages([{ message: response.data.error }]);
          },

          getOrderAddonDetails: function (response) {
            ctrl.notice.showErrorMessages([{ message: response.data.error }]);
          },

          getPayment: function (response) {
            ctrl.notice.showErrorMessages([{ message: response.data.error }]);
          },

          review: function (response) {},

          complete: (response: { status: number; data: PaymentProcessError }) => {
            if (response.status === 409 && response.data.errorCode === 20152) {
              ctrl.getDuplicateOrder();
            } else {
              ctrl.isProcessing = false;

              $scope.$broadcast('Payment::Process::Error', { response: response });

              const messages = response.data.errors
                ? response.data.errors.map((error) => ({ message: error.message.replace(/^.*?:/, '') }))
                : [{ message: response.data.error.replace(/^.*?:/, '') }];

              ctrl.notice.showErrorMessages(messages);
            }
          },

          getDuplicateOrder: function (response) {
            ctrl.error.complete(response);
          },
        },

        onPaymentComplete: function (e, data) {
          ctrl.model.payment = data;
          CheckoutProcessService.setPaymentData(data);
          ctrl.complete();
        },

        onPaymentError: function (e, data) {
          ctrl.isProcessing = false;
          ctrl.notice.showErrorMessages([
            {
              message: data,
            },
          ]);
        },

        onSelectedOrgContactUpdate: function (n, o) {
          if (ctrl.model && n) {
            ctrl.model.contactFirstName = n.firstName;
            ctrl.model.contactLastName = n.lastName;
            ctrl.model.phoneNumber = n.phoneNumber;
            ctrl.model.email = n.email;
            if (n.id) {
              ctrl.model.accountContactId = n.id;
            }
          }
        },

        onContactsTypeaheadNoResults: function (e, data) {},

        onContactsTypeaheadContactNotFound: function (e, data) {},
      };

      $scope.ordersEdit = ctrl;
      ctrl.init();
    },
  ]).name;
